// Initial framing of the map from the fleet itself, so any Pony region works without config.
import type { Position } from "../../shared/api";

export type LngLatBounds = [[number, number], [number, number]];
export interface FleetArea {
  center: Position;
  bounds: LngLatBounds;
}

/** Vehicles farther than this from the anchor are left out of the frame (other city, depot, 0,0 glitch). */
const CLUSTER_KM = 25;
/** Grid cell used to find the busiest place; ~25 km in latitude. */
const CELL_DEG = 0.25;

function km(a: Position, b: Position): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const x =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(x));
}

/**
 * Frame of the busiest place in the fleet. With several regions (one GBFS key, many cities) a median
 * would land between cities, so vehicles are bucketed on a coarse grid and the fullest cell wins.
 */
export function fleetArea(points: Position[]): FleetArea | undefined {
  if (points.length === 0) return undefined;
  const cells = new Map<string, { n: number; lat: number; lng: number }>();
  for (const p of points) {
    const key = `${Math.floor(p.lat / CELL_DEG)}:${Math.floor(p.lng / CELL_DEG)}`;
    const c = cells.get(key) ?? { n: 0, lat: 0, lng: 0 };
    c.n += 1;
    c.lat += p.lat;
    c.lng += p.lng;
    cells.set(key, c);
  }
  let best = { n: 0, lat: 0, lng: 0 };
  for (const c of cells.values()) if (c.n > best.n) best = c;
  const anchor = { lat: best.lat / best.n, lng: best.lng / best.n };
  const kept = points.filter((p) => km(p, anchor) <= CLUSTER_KM);
  const lats = kept.map((p) => p.lat);
  const lngs = kept.map((p) => p.lng);
  const bounds: LngLatBounds = [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
  return {
    center: { lat: (bounds[0][1] + bounds[1][1]) / 2, lng: (bounds[0][0] + bounds[1][0]) / 2 },
    bounds,
  };
}

/** True when some vehicle is within reach of the user, whichever region it belongs to. */
export function isNearFleet(user: Position, points: Position[]): boolean {
  return points.some((p) => km(user, p) <= CLUSTER_KM);
}

/** Bikes "à proximité" of a parking: those within this distance, else the few nearest. */
const NEARBY_KM = 0.5;
const NEAREST = 3;

/** Frame showing a parking zone and the bikes around it (parking sheet, "Voir les vélos à proximité"). */
export function nearbyBounds(center: Position, points: Position[]): LngLatBounds {
  const byDistance = points.map((p) => ({ p, d: km(center, p) })).sort((a, b) => a.d - b.d);
  const within = byDistance.filter((x) => x.d <= NEARBY_KM);
  const chosen = (within.length > 0 ? within : byDistance.slice(0, NEAREST)).map((x) => x.p);
  const all = [center, ...chosen];
  const lngs = all.map((p) => p.lng);
  const lats = all.map((p) => p.lat);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}
